import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { ImSpinner8 } from "react-icons/im";
import backgroundImage from '../../images/header-homepage.webp';
import logo from '../../images/logo_icon.png';
import {
  userSignIn,
} from "../../Common/lib/Session";
import { HiOutlineEye, HiOutlineEyeOff } from "react-icons/hi";
import useDocumentTitle from "../../Common/useDocumentTitle";

export default function SignInForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);

  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const [loading, setLoading] = useState(false); // Used for loading spinner

  useDocumentTitle("Sign In");

  const history = useHistory();

  // Removes errors when user starts editing email/password
  useEffect(() => {
    if (email.trim().length > 0) {
      setEmailError("");
    }
    if (password.trim().length > 0) {
      setPasswordError("");
    }
  }, [email, password]);

  // Joins class names. Used for adding red borders to inputs with errors.
  function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
  }

  // "Validates" form (checks if email and password are empty)
  function validateForm() {
    if (email === "" || password === "") {
      if (email === "") {
        setEmailError("Email required")
      }
      if (password === "") {
        setPasswordError("Password required")
      }
      setLoading(false);
      return false;
    }
    return true;
  }

  // Attempts to login user with provided email and password. Displays error on fail.
  async function attemptLogin() {
    setLoading(true);
    if (validateForm()) {
      const result = userSignIn(email.trim(), password);

      result.then((res) => {
        setLoading(false);
        if (res.success) {
          history.push("/");
        } else {
          setEmailError("Email or password is invalid");
          setPasswordError("Email or password is invalid");
        }
      });
    }
  }

  return (
    <>
      <div className="h-screen md:flex dark:bg-gray-900">
        <div className="flex 2xl:w-2/5 w-full justify-center py-10 items-center bg-white dark:bg-gray-900">
          <div className="w-2/3 md:w-1/2 max-w-md pb-0 sm:pb-32">

            <div className="mt-4 flex justify-center">
              <a href="/">
                <img
                  draggable="false"
                  className="h-16 dark:invert"
                  src={logo}
                  alt="Arctic Research Foundation Logo"
                />
              </a>
            </div>

            <h1 className="mb-8 mt-24 text-2xl font-bold tracking-tight text-gray-900 sm:mt-10 sm:text-4xl text-center dark:text-white">
              Log in
            </h1>

            <form>
              <div className="mb-6 sm:col-span-2">
                <label htmlFor="email" className="items-center flex text-sm sm:text-base font-medium text-gray-700 dark:text-white">
                  Email
                  {emailError.length > 0 &&
                    <p data-cy="email-error" className="italic text-xs sm:text-sm block font-base font-normal text-red-600 dark:text-red-500">
                      &nbsp;- {emailError}
                    </p>
                  }
                </label>
                <div className="mt-1">
                  <input
                    required
                    onChange={(e) => { setEmail(e.target.value); setEmailError("") }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        attemptLogin()
                      }
                    }}
                    type="email"
                    name="email"
                    id="email"
                    className={classNames(emailError.length > 0 ? 'border-red-600 dark:border-red-500' : 'border-gray-300 dark:border-gray-600', 'border outline-none focus:ring-1 text-sm sm:text-base py-3 px-4 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 rounded-md dark:bg-gray-700 dark:text-white')}
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="password" className="items-center flex text-sm sm:text-base font-medium text-gray-700 dark:text-white">
                  Password
                  {passwordError.length > 0 &&
                    <p data-cy="password-error" className="italic text-xs sm:text-sm block font-base font-normal text-red-600 dark:text-red-500">
                      &nbsp;- {passwordError}
                    </p>
                  }
                </label>
                <div className="mt-1 flex justify-end items-center">
                  <input
                    onChange={(e) => { setPassword(e.target.value); setPasswordError("") }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        attemptLogin()
                      }
                    }}
                    required
                    type={showPassword ? "text" : "password"}
                    name="password"
                    id="password"
                    className={classNames(passwordError.length > 0 ? 'border-red-600 dark:border-red-500' : 'border-gray-300 dark:border-gray-600', 'border outline-none focus:ring-1 text-sm sm:text-base py-3 px-4 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 rounded-md dark:bg-gray-700 dark:text-white')}
                  />
                  {showPassword ? (
                    <HiOutlineEyeOff className="absolute justify-end h-7 w-7 cursor-pointer mr-3 text-gray-400" onClick={() => setShowPassword(prevPassword => !prevPassword)} />
                  ) : (
                    <HiOutlineEye className="absolute justify-end h-7 w-7 cursor-pointer mr-3 text-gray-400" onClick={() => setShowPassword(prevPassword => !prevPassword)} />
                  )}
                </div>
              </div>

              <div className="mt-2 text-sm sm:text-base flex justify-end">
                <a href="/forgot-password" className="font-medium text-blue-700 flex items-center dark:text-blue-500">Forgot password?</a>
              </div>

              {/* ----------------- USE THIS IF YOU EVER WANT TO ADD THE 'Remember me' OPTION -----------------*/}
              {/* <div className="mt-6 text-sm sm:text-base flex justify-between">
                <label className="font-medium text-gray-700 flex items-center dark:text-gray-400">
                  <input
                    type="checkbox"
                    className="mr-2 h-5 w-5 border border-gray-300 rounded text-blue-700 focus:ring-blue-700 dark:bg-gray-700 dark:border-gray-600"
                    onChange={() => setShowPassword(prevShowPassword => !prevShowPassword)}
                  />
                  Remember me
                </label>
                <a href="/forgot-password" className="font-medium text-blue-700 flex items-center">Forgot password?</a>
              </div> */}

              <div className="mt-6 flex items-center gap-x-6">
                <button
                  onClick={() => attemptLogin()}
                  data-cy="login"
                  type="button"
                  className="inline-flex items-center justify-center gap-x-2 rounded-md w-full bg-blue-600 py-3.5 px-3.5 text-md font-semibold text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Log In
                  {loading &&
                    <ImSpinner8 className="w-5 h-5 text-white animate-spin" />
                  }
                </button>
              </div>
            </form>

            <div className="mt-4 flex justify-center">
              <p className="text-sm sm:text-base font-medium text-gray-700 dark:text-gray-400">Don't have an account yet? <a href="/signup" data-cy="signup" className="font-medium text-blue-700 dark:text-blue-500">Sign Up <span aria-hidden="true"> &rarr;</span> </a></p>
            </div>
          </div>
        </div>

        <div className="relative overflow-hidden lg:flex w-3/5 justify-around items-center hidden">
          <img
            draggable="false"
            className="aspect-[3/2] w-full bg-gray-50 object-cover lg:absolute lg:inset-0 lg:aspect-auto h-screen"
            src={backgroundImage}
            alt="Arctic plains landscapes"
          />
        </div>
      </div>
    </>
  )
}